import { useLocalStorageValue } from '@react-hookz/web/useLocalStorageValue/index.js';
import { useCallback, useState } from 'react';

import { useWeatherAPI } from '#components/Weather/api/useWeatherAPI';
import { useEnvironment } from '#cutils/context/EnvironmentContext';

import { SingleColumnLayout } from '../SingleColumnLayout/SingleColumnLayout';
import { LoadingSpinner } from '../base/LoadingSpinner/LoadingSpinner';

import { CurrentDayForecast } from './Forecast/CurrentDayForecast';
import { DayForecast } from './Forecast/DayForecast';
import { WeekForecast } from './Forecast/WeekForecast';
import { LocationPicker } from './Location/LocationPicker';
import styles from './Weather.module.scss';
import { WeatherError } from './WeatherError';
import type { WeatherLocation } from './api/useLocationAPI';
import { getRandomDefaultLocation } from './utils';

export function Weather() {
	const { localStorageKeys } = useEnvironment();
	const [selectedDay, setSelectedDay] = useState<string | null>(null);

	const randomDefaultLocation = getRandomDefaultLocation();
	const { set: setLocationInLS, value: locationInLS } = useLocalStorageValue(localStorageKeys.LOCAL_STORAGE_KEY_WEATHER, {
		defaultValue: randomDefaultLocation as WeatherLocation | null,
		initializeWithValue: true,
	});

	const handleLocationChange = useCallback(
		(value: WeatherLocation | null) => {
			setLocationInLS(value);
			setSelectedDay(null);
		},
		[setLocationInLS]
	);

	const { hasError, isLoading, weatherData } = useWeatherAPI(locationInLS);

	if (isLoading) {
		return (
			<SingleColumnLayout contentClassName={styles.contentLayout}>
				<LoadingSpinner theme="dark" />
			</SingleColumnLayout>
		);
	}

	if (hasError || !weatherData) {
		return <WeatherError />;
	}

	const currentDayForecast = weatherData.days[0];
	const selectedDayForecast = weatherData.days.find((day) => day.date.timestamp === selectedDay) ?? weatherData.days[0];

	return (
		<SingleColumnLayout contentClassName={styles.contentLayout}>
			<main className={styles.content} data-theme-area="dark">
				<h1 className="srOnly">Bayern-Wetter</h1>
				<h2 className={`heading3 ${styles.date}`}>
					<time dateTime={weatherData.today.timestamp}>{weatherData.today.humanReadable}</time>
				</h2>

				<LocationPicker className={styles.section} initialLocation={locationInLS} onLocationChange={handleLocationChange} />

				<CurrentDayForecast className={styles.section} forecast={currentDayForecast} />

				<DayForecast className={styles.section} forecast={selectedDayForecast} key={selectedDayForecast.date.timestamp} />

				<WeekForecast
					className={styles.section}
					forecast={weatherData.days}
					selectedDate={selectedDay}
					onChangeSelectedDate={setSelectedDay}
				/>
			</main>
		</SingleColumnLayout>
	);
}
